import { useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import * as localState from '@/components/Analysis/modules/CustomBlocks/state';
import { useCallback } from 'react';
import { PointOfInterestDocument, PointOfInterestTemplateDocument } from '@/types';
import {
  PointOfInterestTemplateTypeEnum,
  usePaintBlockPointsLazyQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useIsolateCustomBlock } from './useIsolateCustomBlock';
import { prismToAnalysisCuboid } from '@/utils/geometry';
import { useCuboidDataHandlers, useCuboidState, useCuboidStyleHandlers } from '../../cuboids/hooks';
import { useAddCuboid } from '@/modules/cuboids/hooks/useAddCuboid';
import { getPoiTemplateKey, upperSnakeCase } from '@/utils';

export const useWorkScopeFlow = () => {
  const structureId = useGetStructureId();
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const structureDefaultAssemblyId = useAtomValue(state.structureDefaultAssemblyId);
  const setEditPointOfInterest = useSetAtom(poiState.editPointOfInterest);
  const setSelectedTemplate = useSetAtom(poiState.selectedTemplate);
  const { deSelectCustomBlock } = useIsolateCustomBlock({});
  const cuboidState = useCuboidState();
  const setIsAddingWorkscope = useSetAtom(cuboidState.isAddingWorkscope);
  const setIsCuboidVisibilityBoxEnabled = useSetAtom(cuboidState.isCuboidVisibilityBoxEnabled);
  const setReferenceCuboid = useSetAtom(cuboidState.referenceCuboid);
  const cuboidDataHandlers = useCuboidDataHandlers();
  const cuboidStyleHandlers = useCuboidStyleHandlers();
  const addCuboid = useAddCuboid(cuboidState, cuboidDataHandlers, cuboidStyleHandlers);
  const selectedCustomBlock = useAtomValue(localState.selectedCustomBlock);

  const [fetchPaintRegionPoints, { loading: fetchPaintRegionPointsLoading }] =
    usePaintBlockPointsLazyQuery();

  const startAddingWorkScope = useCallback(
    async ({ onError }: { onError?: (reason: string) => void }) => {
      const workScopeTemplate = allPointOfInterestTemplates.find(
        ({ type }) => type === PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest
      );

      if (!workScopeTemplate) {
        onError?.('Work Scope template not found');
        return;
      }

      try {
        // Cant proceed if no default assembly is found
        if (!structureDefaultAssemblyId) {
          onError?.('No default assembly found');
          return;
        }

        // Fetching the blocks's 3D data
        const paintRegionResponse = await fetchPaintRegionPoints({
          variables: {
            paintRegionId: selectedCustomBlock?.paintRegionId,
          },
        });

        // Extracting The Prism™
        const prism = paintRegionResponse?.data?.paintRegion?.volume?.inclusion;

        if (!prism || !prism?.z_range || !prism?.xy_polygon) {
          onError?.('Missing 3D data. Please contact support');
          return;
        }

        // Deselecting the custom block to return the view back to the original view
        deSelectCustomBlock();

        const blockCuboid = prismToAnalysisCuboid({
          prismData: prism,
          id: selectedCustomBlock?.paintRegionId || '',
          name: selectedCustomBlock?.name,
        });

        const center = blockCuboid.position;

        addCuboid(blockCuboid);

        setReferenceCuboid(blockCuboid);

        setIsAddingWorkscope(true);

        setIsCuboidVisibilityBoxEnabled(true);

        const newWorkScopePoiData = {
          type: upperSnakeCase(getPoiTemplateKey(workScopeTemplate) ?? ''),
          name: workScopeTemplate.name,
          assembly: { id: structureDefaultAssemblyId, structure: { id: structureId } },
          centerPoint3d: { x: center[0], y: center[1], z: center[2] },
          cameraLocation: { x: center[0], y: center[1], z: center[2] },
        } as PointOfInterestDocument;

        setEditPointOfInterest((current) => ({
          ...current,
          state: 'WaitingForDetail',
          template: workScopeTemplate as PointOfInterestTemplateDocument,
          pointOfInterest: newWorkScopePoiData,
          formState: 'Create',
          customBlockId: selectedCustomBlock?.paintRegionId,
        }));

        setSelectedTemplate(workScopeTemplate as PointOfInterestTemplateDocument);
      } catch {
        onError?.('Error fetching default assembly');
      }
    },
    [
      allPointOfInterestTemplates,
      structureDefaultAssemblyId,
      fetchPaintRegionPoints,
      selectedCustomBlock?.paintRegionId,
      selectedCustomBlock?.name,
      deSelectCustomBlock,
      addCuboid,
      structureId,
      setEditPointOfInterest,
      setSelectedTemplate,
      setReferenceCuboid,
      setIsAddingWorkscope,
      setIsCuboidVisibilityBoxEnabled,
    ]
  );

  const isLoading = fetchPaintRegionPointsLoading;

  return {
    startAddingWorkScope,
    isLoading,
  };
};
